import {
	CodeBlock,
	Endpoint,
	Alert,
	Badge,
	Table,
	ParamList,
	Param,
	Card,
	CardGrid,
	Diagram,
} from '../../components/docs';
import styles from '../../components/docs/docs.module.css';

export default function Sessions() {
	const sessionFieldsColumns = [
		{ key: 'field', header: 'Field' },
		{ key: 'type', header: 'Type' },
		{ key: 'description', header: 'Description' },
	];

	const sessionFieldsData = [
		{
			field: 'sessionId',
			type: 'string',
			description: 'Unique identifier for the session (sess_xxxx)',
		},
		{
			field: 'playerId',
			type: 'string',
			description: 'Your unique identifier for the player',
		},
		{
			field: 'operatorId',
			type: 'string',
			description: 'Your operator account identifier',
		},
		{
			field: 'currency',
			type: 'string',
			description: 'Currency code for the session (USD, EUR, BTC, etc.)',
		},
		{
			field: 'walletMode',
			type: 'string',
			description: 'Either "integrated" or "seamless"',
		},
		{
			field: 'balance',
			type: 'string',
			description: 'Current player balance (in smallest unit)',
		},
		{
			field: 'createdAt',
			type: 'number',
			description: 'Unix timestamp when session was created',
		},
		{
			field: 'expiresAt',
			type: 'number',
			description: 'Unix timestamp when session expires',
		},
	];

	const jwtClaimsColumns = [
		{ key: 'claim', header: 'Claim' },
		{ key: 'description', header: 'Description' },
	];

	const jwtClaimsData = [
		{ claim: 'sub', description: 'Session ID (sess_xxxx)' },
		{ claim: 'pid', description: 'Player ID from your system' },
		{ claim: 'oid', description: 'Operator ID' },
		{ claim: 'cur', description: 'Currency code' },
		{ claim: 'wm', description: 'Wallet mode (int/sml)' },
		{ claim: 'iat', description: 'Issued at timestamp' },
		{ claim: 'exp', description: 'Expiration timestamp' },
	];

	return (
		<div className={styles.docsPage}>
			<h1>Player Sessions</h1>
			<p className={styles.lead}>
				Sessions are the foundation of player authentication in Ringfort Studio.
				Every game interaction requires a valid session token that identifies the
				player and their permissions.
			</p>

			<h2>What is a Session?</h2>
			<p>
				A session represents an authenticated game playing period for a specific
				player. When you create a session, Ringfort generates a secure token that
				the game client uses to authenticate all API requests.
			</p>

			<h3>Session Lifecycle</h3>
			<Diagram
				title="Session Flow"
				steps={[
					{
						label: 'Player Requests Game',
						description: 'User clicks play on your platform',
					},
					{
						label: 'Create Session',
						description: 'Your backend calls Ringfort API',
						highlight: true,
					},
					{
						label: 'Token Received',
						description: 'Ringfort returns session token + URL',
					},
					{
						label: 'Player Redirected',
						description: 'Sent to game with authentication',
						highlight: true,
					},
					{
						label: 'Gameplay Active',
						description: 'Token validates all game actions',
					},
					{
						label: 'Session Ends',
						description: 'Expiration or player exit',
					},
					{
						label: 'Webhook Sent',
						description: 'Notification to your backend',
					},
				]}
			/>

			<h3>Session Properties</h3>
			<Table columns={sessionFieldsColumns} data={sessionFieldsData} />

			<h2>Creating Sessions</h2>
			<Endpoint
				method="POST"
				path="/api/v1/operators/sessions"
				description="Creates a new game session for a player"
			/>

			<h3>Request Parameters</h3>
			<ParamList>
				<Param name="playerId" type="string" required>
					Your unique identifier for the player. This should be consistent
					across sessions so player history can be tracked. Maximum 64
					characters.
				</Param>
				<Param name="currency" type="string" required>
					The currency code for this session. Supported currencies include USD,
					EUR, GBP, BTC, ETH, USDT, and more. Check your operator settings for
					enabled currencies.
				</Param>
				<Param name="walletMode" type="string" required>
					Either <code>integrated</code> for Ringfort-managed balances or{' '}
					<code>seamless</code> for operator wallet callbacks. See{' '}
					<a href="/docs/wallet-modes">Wallet Modes</a> for details.
				</Param>
				<Param name="returnUrl" type="string">
					URL to redirect the player when they exit the game. If not provided,
					the game will show a generic exit screen.
				</Param>
				<Param name="demo" type="boolean">
					Set to <code>true</code> to create a demo session with virtual
					balance. Demo sessions cannot win real money.
				</Param>
				<Param name="metadata" type="object">
					Optional key-value pairs to attach to the session. Useful for tracking
					marketing campaigns or player segments.
				</Param>
			</ParamList>

			<h3>Example Request</h3>
			<CodeBlock language="typescript" title="Create Session">
				{`const response = await fetch('https://api.rngfort.com/api/v1/operators/sessions', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer sk_live_xxxxxxxxxxxxx',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    playerId: 'player_12345',
    currency: 'USD',
    walletMode: 'seamless',
    returnUrl: 'https://yoursite.com/casino',
    metadata: {
      campaign: 'summer_promo',
      vipLevel: 'gold',
    },
  }),
});

const data = await response.json();
console.log(data);`}
			</CodeBlock>

			<h3>Example Response</h3>
			<CodeBlock language="json" title="Response">
				{`{
  "success": true,
  "data": {
    "sessionId": "sess_abc123def456",
    "sessionToken": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJzZXNzX2FiYzEyM2RlZjQ1NiIsInBpZCI6InBsYXllcl8xMjM0NSIsIm9pZCI6Im9wXzk4NzY1IiwiY3VyIjoiVVNEIiwid20iOiJzbWwiLCJpYXQiOjE3MDQwNjcyMDAsImV4cCI6MTcwNDA4MTYwMH0.signature",
    "gameUrl": "https://play.rngfort.com/play/dice?token=sess_abc123def456",
    "expiresAt": 1704081600
  }
}`}
			</CodeBlock>

			<Alert variant="info" title="Game URL vs Session Token">
				The <code>gameUrl</code> includes the session ID as a URL parameter for
				convenience. The <code>sessionToken</code> is a JWT that can be used
				directly with game API endpoints if you are building a custom game
				client.
			</Alert>

			<h2>Session Token Format</h2>
			<p>
				Session tokens are JSON Web Tokens (JWT) signed with HS256. The token
				contains claims that identify the session and player.
			</p>

			<h3>JWT Structure</h3>
			<CodeBlock language="json" title="Decoded JWT Payload">
				{`{
  "sub": "sess_abc123def456",
  "pid": "player_12345",
  "oid": "op_98765",
  "cur": "USD",
  "wm": "sml",
  "iat": 1704067200,
  "exp": 1704081600
}`}
			</CodeBlock>

			<h3>Claims Reference</h3>
			<Table columns={jwtClaimsColumns} data={jwtClaimsData} />

			<Alert variant="warning" title="Do Not Decode Client-Side">
				While JWTs can be decoded without the secret key, you should not rely on
				client-side decoding for security decisions. Always validate tokens
				server-side when processing sensitive operations.
			</Alert>

			<h2>Session Expiration and Renewal</h2>

			<h3>Default Expiration</h3>
			<p>
				Sessions expire after <strong>4 hours</strong> of inactivity by default.
				Active gameplay extends the session automatically.
			</p>

			<h3>Handling Expiration</h3>
			<p>
				When a session expires, the game client will display an expiration message
				and optionally redirect the player to your <code>returnUrl</code>.
			</p>
			<CodeBlock language="typescript" title="Handle Session Expiration">
				{`// In your game embedding code
window.addEventListener('message', (event) => {
  if (event.origin !== 'https://play.rngfort.com') return;

  const { type, data } = event.data;

  switch (type) {
    case 'SESSION_EXPIRED':
      console.log('Session expired:', data.sessionId);
      // Create new session and reload game
      refreshGameSession();
      break;

    case 'SESSION_ERROR':
      console.error('Session error:', data.message);
      // Show error to player
      showErrorMessage(data.message);
      break;

    case 'GAME_EXIT':
      // Player clicked exit button
      window.location.href = '/casino';
      break;
  }
});`}
			</CodeBlock>

			<h3>Session Renewal</h3>
			<p>
				To renew a session before it expires, create a new session for the same
				player. The old session will be invalidated.
			</p>
			<CodeBlock language="typescript" title="Renew Session">
				{`const renewSession = async (playerId: string) => {
  // Creating a new session automatically invalidates any existing session
  const response = await fetch('https://api.rngfort.com/api/v1/operators/sessions', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer sk_live_xxxxxxxxxxxxx',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      playerId,
      currency: 'USD',
      walletMode: 'seamless',
    }),
  });

  const data = await response.json();

  if (data.success) {
    // Redirect player to new game URL
    return data.data.gameUrl;
  }

  throw new Error('Failed to renew session');
};`}
			</CodeBlock>

			<h2>Managing Multiple Sessions</h2>

			<h3>One Active Session Per Player</h3>
			<p>
				Each player can only have <strong>one active session at a time</strong>{' '}
				per operator. Creating a new session for a player will automatically
				invalidate any existing session.
			</p>

			<h3>Concurrent Device Access</h3>
			<p>
				If a player tries to access the game from a second device while an active
				session exists on the first device, the first session will be terminated.
			</p>
			<CodeBlock language="typescript" title="Session Conflict Handling">
				{`// The new session response indicates if an existing session was terminated
const response = await createSession(playerId);

if (response.data.previousSessionTerminated) {
  console.log('Previous session was terminated');
  // Optionally notify the player on their first device
}`}
			</CodeBlock>

			<h3>Getting Active Session</h3>
			<Endpoint
				method="GET"
				path="/api/v1/operators/sessions/:playerId"
				description="Get active session for a player if one exists"
			/>
			<CodeBlock language="typescript" title="Check Existing Session">
				{`const checkExistingSession = async (playerId: string) => {
  const response = await fetch(
    \`https://api.rngfort.com/api/v1/operators/sessions/\${playerId}\`,
    {
      headers: {
        'Authorization': 'Bearer sk_live_xxxxxxxxxxxxx',
      },
    }
  );

  const data = await response.json();

  if (data.success && data.data) {
    // Player has an active session
    return {
      hasSession: true,
      sessionId: data.data.sessionId,
      expiresAt: data.data.expiresAt,
    };
  }

  return { hasSession: false };
};`}
			</CodeBlock>

			<h3>Terminating Sessions</h3>
			<Endpoint
				method="DELETE"
				path="/api/v1/operators/sessions/:sessionId"
				description="Manually terminate an active session"
			/>
			<CodeBlock language="typescript" title="Terminate Session">
				{`const terminateSession = async (sessionId: string) => {
  const response = await fetch(
    \`https://api.rngfort.com/api/v1/operators/sessions/\${sessionId}\`,
    {
      method: 'DELETE',
      headers: {
        'Authorization': 'Bearer sk_live_xxxxxxxxxxxxx',
      },
    }
  );

  const data = await response.json();

  if (data.success) {
    console.log('Session terminated successfully');
    // Player will see session ended screen
  }
};`}
			</CodeBlock>

			<h2>Security Considerations</h2>

			<h3>Token Security</h3>
			<ul>
				<li>
					<strong>Never expose your secret key:</strong> Session creation must
					only happen on your backend
				</li>
				<li>
					<strong>Use HTTPS:</strong> All communication with Ringfort API must
					be over HTTPS
				</li>
				<li>
					<strong>Validate player identity:</strong> Always verify the player is
					authenticated with your system before creating a session
				</li>
				<li>
					<strong>Monitor for abuse:</strong> Track session creation patterns to
					detect potential abuse
				</li>
			</ul>

			<h3>Session Hijacking Prevention</h3>
			<CodeBlock language="typescript" title="Secure Session Creation">
				{`app.post('/api/play-game', async (req, res) => {
  // 1. Verify user is authenticated with YOUR system
  const user = await verifyAuthentication(req);
  if (!user) {
    return res.status(401).json({ error: 'Not authenticated' });
  }

  // 2. Verify user has permissions to play
  if (user.isBanned || user.isRestricted) {
    return res.status(403).json({ error: 'Account restricted' });
  }

  // 3. Rate limit session creation
  const recentSessions = await getRecentSessionCount(user.id);
  if (recentSessions > 10) {
    return res.status(429).json({ error: 'Too many sessions' });
  }

  // 4. Create session with minimal necessary data
  const session = await createRingfortSession({
    playerId: user.id, // Use internal ID, not email
    currency: user.preferredCurrency,
    walletMode: 'seamless',
  });

  // 5. Log session creation for audit
  await logSessionCreation(user.id, session.sessionId, req.ip);

  // 6. Return only necessary data to client
  res.json({
    gameUrl: session.gameUrl,
  });
});`}
			</CodeBlock>

			<h3>IP Binding</h3>
			<p>
				For additional security, you can enable IP binding in your operator
				settings. When enabled, sessions will only be valid from the IP address
				where they were created.
			</p>
			<Alert variant="info" title="IP Binding Considerations">
				IP binding may cause issues for players on mobile networks or VPNs where
				IP addresses change frequently. Consider your player base before enabling
				this feature.
			</Alert>

			<h2>Demo Sessions</h2>
			<p>
				Demo sessions allow players to try games without real money. They are
				perfect for onboarding new players or showcasing games on your website.
			</p>
			<CodeBlock language="typescript" title="Create Demo Session">
				{`const createDemoSession = async (visitorId: string) => {
  const response = await fetch('https://api.rngfort.com/api/v1/operators/sessions', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer sk_live_xxxxxxxxxxxxx',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      playerId: \`demo_\${visitorId}\`,
      currency: 'USD',
      walletMode: 'integrated', // Demo always uses integrated
      demo: true, // Creates demo session
      returnUrl: 'https://yoursite.com/signup',
    }),
  });

  return response.json();
};`}
			</CodeBlock>

			<h3>Demo Session Characteristics</h3>
			<ul>
				<li>
					<Badge variant="sandbox">Demo</Badge> Virtual balance of 10,000 units
				</li>
				<li>No real money wagered or won</li>
				<li>Bets are marked with <code>isDemo: true</code></li>
				<li>Cannot be converted to real money session</li>
				<li>24-hour expiration</li>
			</ul>

			<div className={styles.nextSteps}>
				<h2>Next Steps</h2>
				<CardGrid columns={3}>
					<Card
						title="Wallet Modes"
						description="Learn about integrated vs seamless wallet modes"
						href="/docs/wallet-modes"
					/>
					<Card
						title="Webhooks"
						description="Handle session events in real-time"
						href="/docs/webhooks"
					/>
					<Card
						title="API Reference"
						description="Complete API documentation"
						href="/docs/api-operators"
					/>
				</CardGrid>
			</div>
		</div>
	);
}
